import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Fade,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ListSubheader,
  InputAdornment,
  TextField,
  IconButton,
  Tooltip,
  Skeleton
} from "@mui/material";

import {
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleFullScreenButton as MrtToggleFullScreenButton,
} from "material-react-table";
import { GlobalDataTable } from "./DataTable/GlobalDataTable";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SearchIcon from "@mui/icons-material/Search";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SummarizeRoundedIcon from "@mui/icons-material/SummarizeRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ValidationHistory from "./ValidationHistory";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa6"
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';


// Data Fetching and Icons
const reportIcons = {
  "HCP Summary": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "HCP Trend": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "Managed Care": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "Product Analysis": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "Territory Scorecard": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "Territory Summary": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "Call Plan Execution": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
  "Apex IC Cross Validation": <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
};
const POLL_INTERVAL = 10000;

/* ── Skeleton for the Recent Runs table ── */
const RunsTableSkeleton = ({ rows = 8, cols = 9 }) => (
  <Box>
    {/* Fake toolbar */}
    <Box sx={{ borderBottom: '1px solid #EEF2F7', px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 58 }}>
      <Box>
        <Skeleton variant="text" width={110} height={18} sx={{ bgcolor: '#F1F5F9', mb: 0.5 }} />
        <Skeleton variant="text" width={230} height={14} sx={{ bgcolor: '#F1F5F9' }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[32, 32, 32, 100].map((w, i) => (
          <Skeleton key={i} variant="rounded" width={w} height={32} sx={{ borderRadius: '8px', bgcolor: '#F1F5F9' }} />
        ))}
      </Box>
    </Box>
    {/* Fake header row */}
    <Box sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #F1F5F9', px: 2, py: 1.2, display: 'flex', gap: 2 }}>
      {['Run Date', 'Pages', 'Geo Filters', 'Geo Names', 'HCP Inputs', 'Status', 'Result', 'Actions', 'Run By'].map((h) => (
        <Skeleton key={h} variant="text" width={h === 'Pages' ? 90 : 65} height={14} sx={{ bgcolor: '#E2E8F0' }} />
      ))}
    </Box>
    {/* Fake data rows */}
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
            {Array.from({ length: cols }).map((_, j) => (
              <td key={j} style={{ padding: '14px 16px' }}>
                <Skeleton variant="text" height={16} sx={{ borderRadius: 1, bgcolor: '#F1F5F9' }} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
   
  </Box>
);

const RunValidation = () => {
  const createHcpSet = () => ({
    id: Date.now() + Math.random(),
    region: "",
    territory: "",
    npi: "",
    lastName: "",
  });

  const [activeTab, setActiveTab] = useState(0);
  const [reports, setReports] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [territoriesList, setTerritoriesList] = useState([]);
  const [todayRuns, setTodayRuns] = useState([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(true);
  const [hcpModalOpen, setHcpModalOpen] = useState(false);
  const [selectedHcpInputs, setSelectedHcpInputs] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const [selectedReports, setSelectedReports] = useState([]);
  const apexIcReport = "Apex IC Cross Validation";
  const isApexSelected = selectedReports.includes(apexIcReport);
  const isApexOnly = selectedReports.length === 1 && isApexSelected;
  const hasApexWithOtherReports = selectedReports.length > 1 && isApexSelected;
  const rollupRequiredPages = ["HCP Summary", "Managed Care", "Territory Summary"];
  const showRollup =
    !isApexOnly &&
    selectedReports.some(report => rollupRequiredPages.includes(report));
  const [selectedRollup, setSelectedRollup] = useState("YTD");
  const [geoLevels, setGeoLevels] = useState({
    National: true,
    Region: false,
    Territory: false,
  });
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [regionSearch, setRegionSearch] = useState("");
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const [territorySearch, setTerritorySearch] = useState("");
  const [hcpRegionSearch, setHcpRegionSearch] = useState("");
  const [hcpTerritorySearch, setHcpTerritorySearch] = useState("");

  const [validationError, setValidationError] = useState(null);

  const [hcpSets, setHcpSets] = useState([createHcpSet()]);
  const [viewResultForRun, setViewResultForRun] = useState(null);
  const [geoSummaryModalOpen, setGeoSummaryModalOpen] = useState(false);
  const [selectedGeoSummary, setSelectedGeoSummary] = useState({
    regions: [],
    territories: [],
  });
  const [geoRegionSearch, setGeoRegionSearch] = useState("");
  const [geoTerritorySearch, setGeoTerritorySearch] = useState("");
  const [showRunSearch, setShowRunSearch] = useState(false);
  const [runSearchValue, setRunSearchValue] = useState("");
  useEffect(() => {
    if (!selectedReports.includes("HCP Summary")) {
      setHcpSets([createHcpSet()]);
      setHcpRegionSearch("");
      setHcpTerritorySearch("");
    }

    if (isApexOnly) {
      setSelectedRollup("TTD");
      setGeoLevels({
        National: false,
        Region: false,
        Territory: true,
      });
      return;
    }

    if (hasApexWithOtherReports) {
      setGeoLevels((prev) => ({
        ...prev,
        National: true,
        Territory: true,
      }));
    }

    if (selectedReports.length === 1 && selectedReports.includes("HCP Summary")) {
      setGeoLevels((prev) => ({
        ...prev,
        Region: false,
        Territory: false,
      }));
    }
  }, [selectedReports, isApexOnly, hasApexWithOtherReports]);

  const isValidationInProgress = todayRuns.some((run) => run.status === "VALIDATING");
  const pageTooltipChipSx = {
    bgcolor: "#EEF4FF",
    border: "1px solid #D6E4FF",
    borderRadius: "14px",
    color: "#1D4ED8",
    fontSize: "0.7rem",
    fontWeight: 600,
    height: 20,
    "& .MuiChip-label": {
      px: "10px",
    },
    "&:hover": {
      bgcolor: "#E0ECFF",
    },
  };

  const pageTooltipSlotProps = {
    tooltip: {
      sx: {
        bgcolor: "#ffffffe1",
        color: "#334155",
        border: "2px solid #E2E8F0",
        borderRadius: "14px",
        boxShadow: "0 10px 30px rgba(2, 6, 23, 0.08)",
        maxWidth: "280px",
        p: "10px",
      },
    },
  };

  const centeredChipColumnHeadSx = {
    textAlign: "center",
    "& .Mui-TableHeadCell-Content": {
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    },
    "& .Mui-TableHeadCell-Content-Labels": {
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto",
      width: "100%",
    },
    "& .Mui-TableHeadCell-Content-Wrapper": {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },
  };

  const centeredChipColumnBodySx = {
    textAlign: "center",
    "& .Mui-TableBodyCell-Content, & [class*='TableBodyCell-Content']": {
      justifyContent: "center",
      margin: "0 auto",
      padding: 0,
      width: "100%",
    },
  };

  const resetConfigForm = () => {
    setSelectedReports(reports ? [...reports] : []);
    setSelectedRollup("YTD");
    setGeoLevels({
      National: true,
      Region: false,
      Territory: false,
    });
    setSelectedRegions([]);
    setSelectedTerritories([]);
    setHcpSets([createHcpSet()]);
    setHcpRegionSearch("");
    setHcpTerritorySearch("");
    setValidationError(null);
  };

  const handleOpenConfigModal = () => setConfigModalOpen(true);
  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
    resetConfigForm();
  };

  const handleViewResult = (row) => {
    setSelectedRunId(row.run_id);
    window.scrollTo(0, 0);
    setViewResultForRun(row);
  };
  const handleOpenGeoSummaryModal = (geoNames = {}) => {
    setSelectedGeoSummary({
      regions: geoNames?.regions || [],
      territories: geoNames?.territories || [],
    });
    setGeoRegionSearch("");
    setGeoTerritorySearch("");
    setGeoSummaryModalOpen(true);
  };

  const handleCloseGeoSummaryModal = () => setGeoSummaryModalOpen(false);
  const handleOpenHcpModal = (inputs) => {
    setSelectedHcpInputs(inputs || []);
    setHcpModalOpen(true);
  };

  const handleCloseHcpModal = () => setHcpModalOpen(false);

  const getCookieMap = () =>
    document.cookie.split("; ").reduce((acc, cookie) => {
      const [key, value] = cookie.split("=");
      if (key) acc[key] = value;
      return acc;
    }, {});

  const fetchMetadata = async () => {
    try {
      const response = await axios.get("/validation/metadata/");
      const fetchedReports = response.data.report_pages || [];
      setReports(fetchedReports);
      setSelectedReports(fetchedReports);
      setRegionsList(response.data.regions || []);
      setTerritoriesList(response.data.territories || []);
    } catch (error) {
      console.error("Error fetching metadata:", error);
      toast.error("Failed to load validation metadata");
      setReports([]);
      setRegionsList([]);
      setTerritoriesList([]);
    }
  };

  const fetchTodayRuns = async ({ showLoader = false } = {}) => {
    if (showLoader) setIsLoadingRuns(true);
    try {
      const response = await axios.get("/validation/runs/?days=14");
      setTodayRuns(response.data.data || []);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching today runs:", error);
      setTodayRuns([]);
      return [];
    } finally {
      if (showLoader) setIsLoadingRuns(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
    window.scrollTo(0, 0);
  }, []);
  const formatDateTime = (dateStr) => {
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, "0");

    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
  };

  useEffect(() => {
    fetchTodayRuns({ showLoader: true });
  }, []);

  useEffect(() => {
    let intervalId;
    const hasValidating = todayRuns?.some((run) => run.status === "VALIDATING");

    if (hasValidating) {
      intervalId = setInterval(() => {
        fetchTodayRuns();
      }, POLL_INTERVAL);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayRuns]);

  // Clear validation error when user interacts with the form
  useEffect(() => {
    if (validationError) {
      setValidationError(null);
    }
  }, [
    selectedReports,
    geoLevels,
    selectedRegions,
    selectedTerritories,
    hcpSets,
  ]);

  const handleRegionChange = (event) => {
    const value = event.target.value;
    if (value.includes("all")) {
      setSelectedRegions(
        selectedRegions.length === regionsList.length ? [] : [...regionsList],
      );
    } else {
      setSelectedRegions(typeof value === "string" ? value.split(",") : value);
    }
  };

  const handleTerritoryChange = (event) => {
    const value = event.target.value;
    if (value.includes("all")) {
      setSelectedTerritories(
        selectedTerritories.length === territoriesList.length
          ? []
          : [...territoriesList],
      );
    } else {
      setSelectedTerritories(
        typeof value === "string" ? value.split(",") : value,
      );
    }
  };

  const handleRun = async () => {
    setValidationError(null);

    if (selectedReports.length === 0) {
      setValidationError("Please select at least one report page");
      return;
    }

    if (showRollup && !selectedRollup) {
      setValidationError("Please select a rollup");
      return;
    }

    const enabledGeo = Object.entries(geoLevels)
      .filter(([_, enabled]) => enabled)
      .map(([key]) => (key === "National" ? "Nation" : key));
    if (enabledGeo.length === 0) {
      setValidationError("Please enable at least one geography level");
      return;
    }

    if (geoLevels.Region && selectedRegions.length === 0) {
      setValidationError("Please select at least one region");
      return;
    }

    if (geoLevels.Territory && selectedTerritories.length === 0) {
      setValidationError("Please select at least one territory");
      return;
    }

    let hcpDetail = [];
    if (selectedReports.includes("HCP Summary")) {
      for (let i = 0; i < hcpSets.length; i++) {
        const set = hcpSets[i];
        const isRegionValid =
          set.region && String(set.region).trim().length > 0;
        const isTerritoryValid =
          set.territory && String(set.territory).trim().length > 0;
        const isNpiValid = set.npi && String(set.npi).trim().length > 0;
        const isLastNameValid =
          set.lastName && String(set.lastName).trim().length > 0;

        if (
          !isRegionValid ||
          !isTerritoryValid ||
          !isNpiValid ||
          !isLastNameValid
        ) {
          setValidationError(`Please fill all fields for HCP set #${i + 1}`);
          return;
        }
        hcpDetail.push({
          region: set.region,
          territory: set.territory,
          lastname: set.lastName,
          npi: set.npi,
        });
      }
    }

    const payload = {
      report_pages: selectedReports,
      rollup: isApexOnly ? "TTD" : showRollup ? selectedRollup : "",
      geo_levels: enabledGeo,
      regions: selectedRegions,
      territories: selectedTerritories,
      hcp_summary_detail: hcpDetail,
    };

    setIsRunning(true);
    try {
      const cookies = getCookieMap();
      const headers = {
        "Content-Type": "application/json",
        ...(cookies.csrftoken ? { "X-CSRFToken": cookies.csrftoken } : {}),
      };
      const response = await axios.post("/trigger-prefect-flow/", payload, {
        headers,
      });
      toast.success(
        response.data.message || "Validation triggered successfully",
      );
      handleCloseConfigModal();

      // Fetch multiple times initially to catch the new run from the database
      fetchTodayRuns();
      setTimeout(fetchTodayRuns, 1500);
      setTimeout(fetchTodayRuns, 3500);
      setTimeout(fetchTodayRuns, 6000);
    } catch (error) {
      console.error("Trigger Error:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to trigger validation";
      setValidationError(msg);
    } finally {
      setIsRunning(false);
    }
  };

  const handleRerun = (run) => {
    // 1. Set Reports
    setSelectedReports(run.pages || []);
    setSelectedRollup(run.rollup || "");

    // 2. Set Geography Levels
    const levels = { National: false, Region: false, Territory: false };
    run.geo_levels?.forEach((l) => {
      if (l === "Nation") levels.National = true;
      else if (l === "Region") levels.Region = true;
      else if (l === "Territory") levels.Territory = true;
    });
    setGeoLevels(levels);

    // 3. Set Geography Selections
    setSelectedRegions(run.geo_names?.regions || []);
    setSelectedTerritories(run.geo_names?.territories || []);

    // 4. Set HCP Sets
    if (run.hcp_inputs && run.hcp_inputs.length > 0) {
      setHcpSets(
        run.hcp_inputs.map((input) => ({
          id: Date.now() + Math.random(),
          region: input.region || "",
          territory: input.territory || "",
          npi: input.npi || "",
          lastName: input.lastname || input.lastName || "",
        })),
      );
    } else {
      setHcpSets([
        { id: Date.now(), region: "", territory: "", npi: "", lastName: "" },
      ]);
    }

    setValidationError(null);
    setConfigModalOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedRunId(null); // Clear specific run filter when manually switching tabs
  };

  const addHcpSet = () => {
    if (hcpSets.length >= 10) {
      setValidationError("You can only add up to 10 HCP sets limit.");
      return;
    }
    setHcpSets((prev) => [
      ...prev,
      { id: Date.now(), region: "", territory: "", npi: "", lastName: "" },
    ]);
  };

  const removeHcpSet = (id) => {
    if (hcpSets.length > 1) {
      setHcpSets((prev) => prev.filter((set) => set.id !== id));
    }
  };

  const updateHcpSet = (id, field, value) => {
    setHcpSets((prev) =>
      prev.map((set) => (set.id === id ? { ...set, [field]: value } : set)),
    );
  };

  const toggleGeoLevel = (level) => {
    setGeoLevels((prev) => ({ ...prev, [level]: !prev[level] }));
  };

  const renderStatusChip = (status) => {
    const styles = {
      COMPLETED: {
        bg: "#F0FDF4",
        color: "#166534",
        border: "#DCFCE7",
        icon: (
          <CheckCircleRoundedIcon sx={{ fontSize: 14, color: "#22C55E" }} />
        ),
        label: "Completed",
      },
      VALIDATING: {
        bg: "#EFF6FF",
        color: "#1E40AF",
        border: "#DBEAFE",
        icon: (
          <AutorenewRoundedIcon
            sx={{
              fontSize: 14,
              color: "#3B82F6",
              animation: "spin 2s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        ),
        label: "In Progress",
      },
      FAILED: {
        bg: "#FEF2F2",
        color: "#991B1B",
        border: "#FEE2E2",
        icon: <CancelRoundedIcon sx={{ fontSize: 14, color: "#EF4444" }} />,
        label: "Failure",
      },
      COMPLETED_WITH_ERRORS: {
        bg: "#FEF9C3",
        color: "#78350F",
        border: "#FEF08A",
        icon: <WarningRoundedIcon sx={{ fontSize: 14, color: "#F59E0B" }} />,
        label: "Completed(Errors)",
      },
    };

    const config = styles[status?.toUpperCase()] || styles["VALIDATING"];

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: "20px",
          bgcolor: config.bg,
          color: config.color,
          border: `1.5px solid ${config.border}`,
          fontSize: "0.75rem",
          fontWeight: 600,
        }}
      >
        {config.icon}
        {config.label}
      </Box>
    );
  };
  const renderCenteredHeader = useCallback(
    (label, sx = {}) =>
      ({ column }) => (
        <Box
          component="span"
          onClick={column.getToggleSortingHandler?.()}
          sx={{
            alignItems: "center",
            cursor: column.getCanSort?.() ? "pointer" : "default",
            display: "flex",
            gap: "3px",
            height: "32px",
            justifyContent: "center",
            lineHeight: "16px",
            width: "100%",
            ...sx,
          }}
        >
          <Box component="span" sx={{ flex: "0 0 auto" }}>
            {label}
          </Box>
          <FaSort
            style={{
              color: "#9CA3AF",
              flexShrink: 0,
              fontSize: "11px",
            }}
          />
        </Box>
      ),
    [],
  );


  const todayRunsColumns = useMemo(
    () => [
     {
  accessorKey: "run_time",
  header: "Run Date",
  Header: renderCenteredHeader("Run Date"),
  filterPlaceholder: "Search by run date",
  minSize: 100,
  size: 110,
  maxSize: 110,
  paddingLeft: "18px",

  muiTableBodyCellProps: {
    sx: {
      textAlign: "left !important",
      pl: "8px !important",
      pr: "8px !important",
      "& > *": {
        justifyContent: "flex-start !important",
        marginLeft: "0 !important",
        textAlign: "left !important",
      },
      "& .Mui-TableBodyCell-Content, & [class*='TableBodyCell-Content']": {
        justifyContent: "flex-start !important",
        margin: "0 !important",
        padding: "0 !important",
        textAlign: "left !important",
        width: "100%",
      },
    },
  },

  Cell: ({ row }) => {
    const formatted = formatDateTime(row.original?.run_time);
    const [date, time] = formatted.split(" ");

    return (
      <Tooltip title={`${date} ${time}`} arrow>
        <Typography
          sx={{
            color: "#1E293B",
            fontSize: "0.8rem",
            lineHeight: 1.25,
            textAlign: "left",
            width: "100%",
            cursor: "pointer",
          }}
        >
          {date}
        </Typography>
      </Tooltip>
    );
  }},
      {
        accessorKey: "pages",
        header: "Pages",
        Header: renderCenteredHeader("Pages", {
          justifyContent: "center",
        }),
        filterPlaceholder: "Search by pages",
        minSize: 225,
        size: 225,
        maxSize: 225,
        muiTableHeadCellProps: {
          sx: centeredChipColumnHeadSx,
        },
        muiTableBodyCellProps: {
          sx: {
            ...centeredChipColumnBodySx,
            pl: "8px !important",
            pr: "8px !important",
            // paddingLeft: "8px !important",
            paddingRight: "8px !important",
          },
        },
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: "3px",
              justifyContent: "center",
              rowGap: "2px",
              ml: 0,
              width: "100%",
            }}
          >
            {row.original.pages?.slice(0, 2).map((page, index) => (
              <Chip
                key={index}
                label={page}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor: "#F1F5F9",
                  "& .MuiChip-label": {
                    px: "8px",
                  },
                }}
              />
            ))}
            {row.original.pages?.length > 2 && (
              <Tooltip
                placement="top"
                slotProps={pageTooltipSlotProps}
                title={
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      maxWidth: "244px",
                    }}
                  >
                    {row.original.pages.map((page, index) => (
                      <Chip
                        key={`${page}-${index}`}
                        label={page}
                        size="small"
                        sx={pageTooltipChipSx}
                      />
                    ))}
                  </Box>
                }
              >
                <Chip
                  label={`+${row.original.pages.length - 2}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    bgcolor: "#EFF6FF",
                    color: "#2563EB",
                    cursor: "pointer",
                    "& .MuiChip-label": {
                      px: "7px",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        accessorKey: "rollup",
        id: "rollup",
        header: "Rollup",
        Header: renderCenteredHeader("Rollup"),
        filterPlaceholder: "Search by rollup",
        minSize: 120,
        size: 120,
        maxSize: 120,
        muiTableHeadCellProps: {
          sx: centeredChipColumnHeadSx,
        },
        muiTableBodyCellProps: {
          sx: centeredChipColumnBodySx,
        },
        Cell: ({ row }) => {
          const rollup = row.original.rollup;
          return rollup ? (
            <Chip
              label={rollup}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 600,
                bgcolor: "#E0F2FE",
                color: "#0284C7",
              }}
            />
          ) : (
            <Box sx={{ textAlign: "center", width: "100%" }}>-</Box>
          );
        },
      },
      {
        accessorKey: "geo_levels",
        header: "Geo Filters",
        Header: renderCenteredHeader("Geo Filters"),
        filterPlaceholder: "Search by geo filter",
        minSize: 170,
        size: 170,
        maxSize: 170,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {row.original.geo_levels?.map((level, index) => (
              <Typography
                key={index}
                sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}
              >
                {level}
                {index < row.original.geo_levels.length - 1 ? "," : ""}
              </Typography>
            )) || "-"}
          </Box>
        ),
      },
      {
        accessorKey: "geo_names",
        header: "Geo Names",
        Header: renderCenteredHeader("Geo Names"),
        filterPlaceholder: "Search by geo names",
        minSize: 160,
        size: 160,
        maxSize: 160,
        muiTableBodyCellProps: {
          sx: {
            "& .Mui-TableBodyCell-Content, & [class*='TableBodyCell-Content']":
            {
              width: "100%",
            },
          },
        },
        Cell: ({ row }) => {
          const regionsCount = row.original.geo_names?.regions?.length || 0;
          const territoriesCount =
            row.original.geo_names?.territories?.length || 0;
          const hasGeoNames = regionsCount > 0 || territoriesCount > 0;
          const regionLabel = regionsCount > 1 ? "regions" : "region";
          const territoryLabel =
            territoriesCount > 1 ? "territories" : "territory";

          return hasGeoNames ? (
            <Button
              onClick={() => handleOpenGeoSummaryModal(row.original.geo_names)}
              sx={{
                p: 0,
                minWidth: 0,
                textTransform: "none",
                fontSize: "0.75rem",
                color: "#64748B",
                fontWeight: 600,
                justifyContent: "flex-start",
                "&:hover": {
                  bgcolor: "transparent",
                  color: "#2563EB",
                  textDecoration: "underline",
                },
              }}
            >
              {`${regionsCount} ${regionLabel} \u00b7 ${territoriesCount} ${territoryLabel}`}
            </Button>
          ) : (
            <Typography
              sx={{
                color: "#64748B",
                fontSize: "0.75rem",
                textAlign: "center",
                width: "100%",
              }}
            >
              -
            </Typography>
          );
        },
      },
      {
        accessorKey: "hcp_inputs",
        header: "Hcp Inputs",
        Header: renderCenteredHeader("Hcp Inputs"),
        filterPlaceholder: "Search by HCP inputs",
        minSize: 120,
        size: 120,
        maxSize: 120,
        muiTableHeadCellProps: {
          sx: {
            px: "8px !important",
            paddingLeft: "8px !important",
            paddingRight: "8px !important",
            textAlign: "center",
            "& .Mui-TableHeadCell-Content": {
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            },
            "& .Mui-TableHeadCell-Content-Labels": {
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              width: "100%",
            },
            "& .Mui-TableHeadCell-Content-Wrapper": {
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              width: "100%",
            },
          },
        },
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
            px: "8px !important",
            paddingLeft: "8px !important",
            paddingRight: "8px !important",
            "& .Mui-TableBodyCell-Content, & [class*='TableBodyCell-Content']":
            {
              justifyContent: "center",
              margin: "0 auto",
              padding: 0,
              width: "100%",
            },
          },
        },
        Cell: ({ row }) =>
          row.original.hcp_inputs?.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                ml: 0,
                width: "100%",
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleOpenHcpModal(row.original.hcp_inputs)}
                sx={{ color: "#2563EB", mx: "auto" }}
              >
                <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ) : (
            <Typography
              sx={{
                color: "#CBD5E1",
                fontSize: "0.75rem",
                ml: 0,
                textAlign: "center",
                width: "100%",
              }}
            >
              -
            </Typography>
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Header: renderCenteredHeader("Status"),
        filterPlaceholder: "Search by status",
        minSize: 140,
        size: 140,
        maxSize: 140,
        muiTableHeadCellProps: {
          sx: centeredChipColumnHeadSx,
        },
        muiTableBodyCellProps: {
          sx: centeredChipColumnBodySx,
        },
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            {renderStatusChip(row.original.status)}
          </Box>
        ),
      },
      {
        accessorKey: "result",
        header: "Result",
        Header: renderCenteredHeader("Result"),
        filterPlaceholder: "Search by result",
        minSize: 90,
        size: 90,
        maxSize: 90,
        Cell: ({ row }) =>
          row.original?.status !== "VALIDATING" ? (
            <Button
              size="small"
              onClick={() => handleViewResult(row.original)}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                color: "#0576fc",
                fontSize: "0.75rem",
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
                p: 0,
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              View Result
            </Button>
          ) : (
            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>
              Pending...
            </Typography>
          ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        Header: renderCenteredHeader("Actions"),
        filterPlaceholder: "Search by actions",
        minSize: 95,
        size: 95,
        maxSize: 95,
        Cell: ({ row }) => (
          <Tooltip
            title={isValidationInProgress ? "Already validation in progress" : ""}
           
          >
            <span>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleRerun(row.original)}
                disabled={isValidationInProgress}
                startIcon={
                  <ReplayRoundedIcon sx={{ fontSize: "14px !important" }} />
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  bgcolor: isValidationInProgress ? "#E2E8F0" : "#EFF6FF",
                  color: isValidationInProgress ? "#94A3B8" : "#0576fc",
                  boxShadow: "none",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.5,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: isValidationInProgress ? "#E2E8F0" : "#DBEAFE", boxShadow: "none" },
                  "& .MuiButton-startIcon": { mr: 0.5 },
                  "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" }
                }}
              >
                Rerun
              </Button>
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "validated_by",
        header: "Run By",
        Header: renderCenteredHeader("Run By"),
        filterPlaceholder: "Search by run by",
        minSize: 110,
        size: 110,
        maxSize: 110,
        Cell: ({ row }) => (
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}
          >
            {row.original.validated_by || "system"}
          </Typography>
        ),
      },
    ],
    [
      formatDateTime,
      handleOpenGeoSummaryModal,
      handleOpenHcpModal,
      handleRerun,
      handleViewResult,
      renderCenteredHeader,
      renderStatusChip,
      isValidationInProgress,
    ],
  );
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        marginX: "0",
        marginTop: "-12px",
        padding: "24px 2%",
        overflowX: "hidden",
      }}
    >
      <Box >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            background: "#fff",
            marginTop: "-0.7rem",
            marginBottom: "1rem",
            padding: "6px 8px",
            borderRadius: "14px",
            boxShadow: "0 4px 12px -2px rgb(0 0 0 / 0.08)",
            border: "1px solid #E2E8F0",
            "& .MuiTab-root": {
              color: "#64748B",
              textTransform: "none",
              fontWeight: 700,
              minHeight: 44,
              borderRadius: "10px",
              transition: "all 0.2s",
              "&:hover": { color: "#1E293B", bgcolor: "#F8FAFC" },
            },
            "& .Mui-selected": {
              color: "#0576FC",
              // bgcolor: "#F8FAFC",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#0576FC",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            // icon={<RocketLaunchRoundedIcon sx={{ fontSize: 18, mr: 1 }} />}
            iconPosition="start"
            label="Validation Hub"
          />
          <Tab
            // icon={<HistoryRoundedIcon sx={{ fontSize: 18, mr: 1 }} />}
            iconPosition="start"
            label="History"
          />
        </Tabs>
        <ToastContainer />

        {activeTab === 0 ? (
          <Fade in={activeTab === 0}>
            <Box>
              {selectedRunId ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="text"
                      onClick={() => {
                        setSelectedRunId(null);
                        setViewResultForRun(null);
                      }}
                      startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18, color: "#475569", mr: 0.5 }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        color: "#000307ff",
                        p: 0,
                        minWidth: "auto",
                        "&:hover": { bgcolor: "transparent", color: "#1D4ED8" },
                      }}
                    >
                      Back
                    </Button>
                  </Box>
                  <ValidationHistory
                    reports={reports}
                    initialRunId={selectedRunId}
                    onClearRunId={() => setSelectedRunId(null)}
                    data={viewResultForRun}
                    onOpenGeoModal={handleOpenGeoSummaryModal}
                    onOpenHcpModal={handleOpenHcpModal}

                  />
                </Box>
              ) : (
                <>
                  {/* Recent Runs Table */}
                  <Card
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid #E2E8F0",
                      overflow: "hidden",
                      boxShadow: "none",
                      bgcolor: "#FFF",
                    }}
                  >
                    {isLoadingRuns ? (
                      <RunsTableSkeleton rows={8} cols={9} />
                    ) : (
                    <GlobalDataTable
                      data={todayRuns}
                      columns={todayRunsColumns}
                      paginator
                      enableTopToolbar
                      enableToolbarInternalActions
                      enableGlobalFilter={false}
                      enableColumnFilters={true}
                      enableColumnActions={false}
                      initialState={{
                        showColumnFilters: false,
                      }}
                      rows={10}
                      dataKey="run_id"
                      style={{ borderRadius: "16px" }}
                      className="p-datatable-striped custom-data-table px-3"
                      muiTablePaperProps={{
                        sx: {
                          border: "none",
                          borderRadius: "12px",
                          bgcolor: "#FFFFFF",
                          flex: "0 0 auto",
                          width: "100%",
                          maxWidth: "100%",
                          '&[style*="position: fixed"]': {
                            borderRadius: 0,
                            width: "100dvw !important",
                            maxWidth: "100dvw !important",
                          },
                          '&[style*="position: fixed"] .MuiTableContainer-root': {
                            width: "100% !important",
                            maxWidth: "100% !important",
                          },
                          '&[style*="position: fixed"] table': {
                            width: "100% !important",
                          },
                        },
                      }}
                      muiTopToolbarProps={{
                        sx: {
                          alignItems: "center",
                          borderBottom: "1px solid #EEF2F7",
                          boxShadow: "none",
                          minHeight: 58,
                          px: 1.25,
                          py: 0.9,
                          "& .MuiBox-root": {
                            alignItems: "center",
                          },
                        },
                      }}
                      renderTopToolbarCustomActions={() => (
                        <Box sx={{ pl: 0.3 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: "#0F172A",
                              fontSize: "15px",
                              lineHeight: 1.25,
                              marginTop: 0,
                            }}
                          >
                            Recent Runs
                          </Typography>
                          <Typography
                            sx={{
                              color: "#64748B",
                              fontWeight: 600,
                              fontSize: "13px",

                            }}
                          >
                            Monitor status and results for the last week.
                          </Typography>
                        </Box>
                      )}
                      renderToolbarInternalActions={({ table }) => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {showRunSearch && (
                            <TextField
                              autoFocus
                              size="small"
                              placeholder="Search"
                              value={runSearchValue}
                              onChange={(event) => {
                                const value = event.target.value;
                                setRunSearchValue(value);
                                table.setGlobalFilter(value || undefined);
                              }}
                              InputProps={{
                                endAdornment: runSearchValue ? (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="Clear search"
                                      size="small"
                                      onClick={() => {
                                        setRunSearchValue("");
                                        table.setGlobalFilter(undefined);
                                      }}
                                      edge="end"
                                    >
                                      <CloseRoundedIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ) : null,
                              }}
                              sx={{
                                width: 190,
                                "& .MuiOutlinedInput-root": {
                                  bgcolor: "#FFFFFF",
                                  borderRadius: "8px",
                                  fontSize: "12.5px",
                                  height: 36,
                                  "& fieldset": {
                                    borderColor: "#DDE3EA",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#CBD5E1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#93C5FD",
                                    borderWidth: "1px",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  py: 0,
                                  "&::placeholder": {
                                    color: "#94A3B8",
                                    opacity: 1,
                                  },
                                },
                                "& .MuiInputAdornment-root": {
                                  mr: -0.5,
                                },
                                "& .MuiIconButton-root": {
                                  color: "#94A3B8",
                                  height: 26,
                                  p: 0.25,
                                  width: 26,
                                },
                                "& .MuiIconButton-root:hover": {
                                  bgcolor: "transparent",
                                  color: "#64748B",
                                },
                                "& .MuiSvgIcon-root": {
                                  fontSize: 18,
                                },
                              }}
                            />
                          )}
                          <IconButton
                            size="small"
                            aria-label={showRunSearch ? "Close search" : "Search runs"}
                            onClick={() => {
                              if (showRunSearch) {
                                setRunSearchValue("");
                                table.setGlobalFilter(undefined);
                                setShowRunSearch(false);
                              } else {
                                setShowRunSearch(true);
                              }
                            }}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "8px",
                              bgcolor: "#FFFFFF",
                              color: "#334155",
                              boxShadow: "none",
                              p: "6px",
                              "&:hover": {
                                bgcolor: "#EFF6FF",
                                color: "#2563EB",
                                boxShadow: "none",
                              },
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                          >
                            {showRunSearch ? <SearchOffRoundedIcon /> : <SearchIcon />}
                          </IconButton>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.2,
                              p: 0,
                            }}
                          >
                            <MrtToggleFiltersButton
                              table={table}
                              sx={{
                                width: 30,
                                height: 30,
                                p: "6px",
                                borderRadius: "8px",
                                color: "#334155",
                                "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                            <MrtShowHideColumnsButton
                              table={table}
                              sx={{
                                width: 30,
                                height: 30,
                                p: "6px",
                                borderRadius: "8px",
                                color: "#334155",
                                "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                            <MrtToggleDensePaddingButton
                              table={table}
                              sx={{
                                width: 30,
                                height: 30,
                                p: "6px",
                                borderRadius: "8px",
                                color: "#334155",
                                "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                            <MrtToggleFullScreenButton
                            title={table.getState().isFullScreen ? "Exit Full Screen" : "Full Screen"}
                              table={table}
                              sx={{
                                width: 30,
                                height: 30,
                                p: "6px",
                                borderRadius: "8px",
                                color: "#334155",
                                "&:hover": { bgcolor: "#EFF6FF", color: "#2563EB" },
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                          </Box>
                          <Tooltip
                            title={isValidationInProgress ? "Already validation in progress" : ""}
                            // slotProps={{ tooltip: { sx: { bgcolor: '#1E293B', color: '#F8FAFC', fontSize: '0.72rem', fontWeight: 500, borderRadius: '8px', px: 1.5, py: 0.75, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } } }}
                          >
                            <span>
                              <Button
                                variant="contained"
                                onClick={handleOpenConfigModal}
                                disabled={isValidationInProgress}
                                sx={{
                                  borderRadius: "12px",
                                  px: 2.4,
                                  py: 1.05,
                                  mr: 0,
                                  bgcolor: isValidationInProgress ? "#E2E8F0" : "#2563EB",
                                  textTransform: "none",
                                  fontWeight: 800,
                                  boxShadow: "0 8px 16px rgba(37, 99, 235, 0.18)",
                                  "&:hover": {
                                    bgcolor: isValidationInProgress ? "#E2E8F0" : "#1E40AF",
                                    boxShadow: "0 10px 20px rgba(37, 99, 235, 0.22)",
                                  },
                                  "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" }
                                }}
                              >
                                Run Validation
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                      muiTableContainerProps={{
                        sx: {
                          borderTop: "none",
                          maxHeight: "520px",
                          bgcolor: "#FFFFFF",
                          flex: "0 0 auto",
                          width: "100%",
                          maxWidth: "100%",
                          overflowX: "auto",
                        },
                      }}
                      muiTableProps={{
                        sx: {
                          tableLayout: "auto",
                          width: "100%",
                          "& thead tr": {
                            height: "40px !important",
                            maxHeight: "40px !important",
                            minHeight: "40px !important",
                          },
                          "& thead th": {
                            paddingTop: "6px !important",
                            paddingBottom: "6px !important",
                          },
                          "& thead th [class*='TableHeadCell-Content']": {
                            alignItems: "center !important",
                            height: "32px !important",
                            maxHeight: "32px !important",
                            minHeight: "32px !important",
                          },
                          "& thead th [class*='TableHeadCell-Content-Labels']": {
                            alignItems: "center !important",
                            height: "32px !important",
                            maxHeight: "32px !important",
                            minHeight: "32px !important",
                          },
                          "& thead th:first-of-type": {
                            paddingLeft: "18px !important",
                            paddingRight: "8px !important",
                            textAlign: "left !important",
                          },
                          "& tbody td:first-of-type": {
                            paddingLeft: "18px !important",
                            paddingRight: "8px !important",
                            textAlign: "left !important",
                          },
                          "& thead th:nth-of-type(2), & tbody td:nth-of-type(2)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(3), & tbody td:nth-of-type(3)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(4), & tbody td:nth-of-type(4)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(5), & tbody td:nth-of-type(5)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(6), & tbody td:nth-of-type(6)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(7), & tbody td:nth-of-type(7)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(8), & tbody td:nth-of-type(8)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(9), & tbody td:nth-of-type(9)": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                          },
                          "& thead th:last-of-type, & tbody td:last-of-type": {
                            paddingRight: "8px !important",
                          },
                          "& thead th:nth-of-type(2), & tbody td:nth-of-type(2), & thead th:nth-of-type(3), & tbody td:nth-of-type(3), & thead th:nth-of-type(4), & tbody td:nth-of-type(4), & thead th:nth-of-type(5), & tbody td:nth-of-type(5), & thead th:nth-of-type(6), & tbody td:nth-of-type(6), & thead th:nth-of-type(7), & tbody td:nth-of-type(7), & thead th:nth-of-type(8), & tbody td:nth-of-type(8), & thead th:nth-of-type(9), & tbody td:nth-of-type(9)":
                          {
                            borderRight: "none !important",
                          },
                          "& tbody td:first-of-type .Mui-TableBodyCell-Content, & tbody td:first-of-type [class*='TableBodyCell-Content']":
                          {
                            justifyContent: "flex-start !important",
                            margin: "0 !important",
                            padding: "0 !important",
                            textAlign: "left !important",
                            width: "100%",
                          },
                          "& tbody td:first-of-type .MuiTypography-root": {
                            marginLeft: "0 !important",
                            textAlign: "left !important",
                          },
                          "& tbody td": {
                            paddingLeft: "8px !important",
                            paddingRight: "8px !important",
                            verticalAlign: "middle !important",
                          },
                          "& tbody td [class*='TableBodyCell-Content']": {
                            alignItems: "center !important",
                            minHeight: "0 !important",
                          },
                        },
                      }}
                      muiTableHeadCellProps={{
                        sx: {
                          bgcolor: "#F8FAFC",
                          boxShadow: "none !important",
                          color: "#242627 !important",
                          fontSize: "12px",
                          fontWeight: 700,
                          paddingLeft: "8px !important",
                          paddingRight: "8px !important",
                          textTransform: "none",
                          "& .Mui-TableHeadCell-Content": {
                            alignItems: "center !important",
                            height: "32px !important",
                            maxHeight: "32px !important",
                            minHeight: "32px !important",
                          },
                          "& .Mui-TableHeadCell-Content-Labels": {
                            alignItems: "center !important",
                            height: "32px !important",
                            maxHeight: "32px !important",
                            minHeight: "32px !important",
                          },
                        },
                      }}
                      muiTableHeadRowProps={{
                        sx: {
                          boxShadow: "none !important",
                          height: "40px !important",
                          maxHeight: "40px !important",
                          minHeight: "40px !important",
                        },
                      }}
                      muiTableBodyCellProps={{
                        sx: {
                          bgcolor: "#FFFFFF",
                          borderBottom: "1px solid #EEF0F3",
                          borderRight: "none",
                          color: "#1F2937",
                          fontSize: "12px",
                          fontWeight: 500,
                          paddingLeft: "8px !important",
                          paddingRight: "8px !important",
                          verticalAlign: "middle",
                          "& .Mui-TableBodyCell-Content, & [class*='TableBodyCell-Content']":
                          {
                            alignItems: "center",
                            minHeight: "0 !important",
                          },
                        },
                      }}
                      muiBottomToolbarProps={{
                        sx: {
                          borderTop: "1px solid #D7DCE2",
                          boxShadow: "none",
                          "& .MuiTablePagination-root .MuiTablePagination-selectLabel, & .MuiTablePagination-root .MuiTablePagination-displayedRows":
                          {
                            fontSize: "9px !important",
                          },
                          "& .MuiTablePagination-root .MuiInputLabel-root": {
                            fontSize: "11px !important",
                            lineHeight: "14px !important",
                          },
                          "& .MuiTablePagination-root .MuiSelect-select, & .MuiTablePagination-root .MuiTypography-root":
                          {
                            fontSize: "13px !important",
                          },
                        },
                      }}
                      muiPaginationProps={{
                        sx: {
                          "& .MuiTablePagination-selectLabel": {
                            fontSize: "9px !important",
                          },
                          "& .MuiTablePagination-displayedRows": {
                            fontSize: "9px !important",
                          },
                        },
                      }}
                      globalFilterFields={[
                        "run_time",
                        "pages",
                        "geo_levels",
                        "geo_names.regions",
                        "geo_names.territories",
                        "status",
                        "validated_by",
                      ]}
                      paginatorLeft={`Total Records: ${todayRuns.length}`}
                      emptyMessage={
                        <Box sx={{ py: 5, textAlign: "center" }}>
                          <HistoryRoundedIcon
                            sx={{ fontSize: 48, color: "#E2E8F0", mb: 1 }}
                          />
                          <Typography sx={{ color: "#94A3B8", fontWeight: 600 }}>
                            No runs found for the last 14 days
                          </Typography>
                        </Box>
                      }
                    />
                    )}

                  </Card>
                </>
              )}

              {/* Validation Configuration Modal */}
              <Dialog
                open={configModalOpen}
                onClose={handleCloseConfigModal}
                maxWidth="md"
                fullWidth
                scroll="paper"
                BackdropProps={{
                  sx: {
                    bgcolor: "rgba(15, 23, 42, 0.45)",
                    backdropFilter: "blur(6px)",
                  },
                }}
                PaperProps={{
                  sx: {
                    borderRadius: "16px",
                    border: "1px solid #E2E8F0",
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 12px 36px rgba(15, 23, 42, 0.12)",
                    overflow: "hidden",
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    px: 3,
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #F1F5F9",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1E293B",
                        fontSize: "14px",
                        lineHeight: 1.4,
                        mb: 0.25,
                      }}
                    >
                      Validation Configuration
                    </Typography>
                    <Typography
                      sx={{
                        color: "#94A3B8",
                        fontWeight: 400,
                        fontSize: "11.5px",
                        lineHeight: 1.3,
                      }}
                    >
                      Set up and trigger a new validation flow
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleCloseConfigModal}
                    size="small"
                    sx={{
                      color: "#94A3B8",
                      "&:hover": { bgcolor: "#F1F5F9", color: "#475569" },
                    }}
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </DialogTitle>
                <DialogContent
                  sx={{
                    px: 3,
                    py: 2,
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    bgcolor: "#F8FAFC",
                  }}
                >
                  {/* Step 1: Report Pages */}
                  <Box sx={{ mb: 2, mt: 0.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: "16px",
                        border: "1px solid #EEF2F7",
                        bgcolor: "#FFFFFF",
                        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "#DBEAFE",
                          color: "#0576FC",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          flexShrink: 0,
                          fontSize: "0.8rem",
                          mt: 2,
                        }}
                      >
                        1
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography

                          sx={{
                            fontWeight: 500,
                            color: "#1E293B",
                            lineHeight: 1.2,
                            fontSize: "13px"

                          }}
                        >
                          Report pages
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontWeight: 500,
                            display: "block",
                            mb: 2,
                            fontSize: "11px"

                          }}
                        >
                          Select one or more pages to validate.
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: "#64748B" }}
                          >
                            {selectedReports.length} of {reports.length} enabled
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => {
                              const allSelected =
                                reports.length > 0 &&
                                selectedReports.length === reports.length;
                              if (allSelected) setSelectedReports([]);
                              else setSelectedReports([...reports]);
                            }}
                            disabled={reports.length === 0}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.72rem",
                              borderRadius: "999px",
                              minWidth: 0,
                              px: 1.5,
                              py: 0.6,
                              minHeight: 28,
                              border: "1px solid",
                              borderColor:
                                reports.length > 0 &&
                                  selectedReports.length === reports.length
                                  ? "#E2E8F0"
                                  : "#BFDBFE",
                              color:
                                reports.length > 0 &&
                                  selectedReports.length === reports.length
                                  ? "#64748B"
                                  : "#1D4ED8",
                              bgcolor:
                                reports.length > 0 &&
                                  selectedReports.length === reports.length
                                  ? "#FFFFFF"
                                  : "#EFF6FF",
                              "&:hover": {
                                bgcolor:
                                  reports.length > 0 &&
                                    selectedReports.length === reports.length
                                    ? "#F8FAFC"
                                    : "#DBEAFE",
                              },
                              "&.Mui-disabled": {
                                color: "#CBD5E1",
                                borderColor: "#E2E8F0",
                                bgcolor: "#FFFFFF",
                              },
                            }}
                          >
                            {reports.length > 0 &&
                              selectedReports.length === reports.length
                              ? "Disable all"
                              : "Enable all"}
                          </Button>
                        </Box>

                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}
                        >
                          {reports.map((report) => {
                            const isSelected = selectedReports.includes(report);
                            return (
                              <Chip
                                key={report}
                                icon={reportIcons[report] || <SummarizeRoundedIcon sx={{ fontSize: 18 }} />}
                                label={report}
                                onClick={() => {
                                  setSelectedReports((prev) =>
                                    prev.includes(report)
                                      ? prev.filter((r) => r !== report)
                                      : [...prev, report],
                                  );
                                }}
                                sx={{
                                  borderRadius: "14px",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  bgcolor: isSelected ? "#EFF6FF" : "#FFFFFF",
                                  color: isSelected ? "#1D4ED8" : "#334155",
                                  border: "1px solid",
                                  borderColor: isSelected ? "#BFDBFE" : "#E2E8F0",
                                  height: 36,
                                  px: 1.2,
                                  transition: "all 0.2s",
                                  "& .MuiChip-icon": {
                                    color: isSelected ? "#1D4ED8" : "#64748B",
                                  },
                                  "&:hover": {
                                    bgcolor: isSelected ? "#DBEAFE" : "#F8FAFC",
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Step 2: Rollup */}
                  {showRollup && (
                  <Box sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: "16px",
                        border: "1px solid #EEF2F7",
                        bgcolor: "#FFFFFF",
                        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "#DBEAFE",
                          color: "#0576FC",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          flexShrink: 0,
                          fontSize: "0.8rem",
                          mt: 0.2,
                        }}
                      >
                        2
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "#1E293B",
                            lineHeight: 1.2,
                            mb: 0.5,
                            fontSize: "13px"
                          }}
                        >
                          Rollup
                        </Typography>
                        <Typography
                          sx={{
                            color: "#64748B",
                            fontWeight: 500,
                            display: "block",
                            mb: 2,
                            fontSize: "11px"
                          }}
                        >
                          Select a rollup.
                        </Typography>
                        
                        <Select
                          fullWidth
                          size="small"
                          value={selectedRollup}
                          displayEmpty
                          onChange={(event) => setSelectedRollup(event.target.value)}
                          input={
                            <OutlinedInput
                              sx={{
                                borderRadius: "8px",
                                bgcolor: "#FFF",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1976d2",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1565c0",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1976d2",
                                },
                              }}
                            />
                          }
                          renderValue={(selected) => (
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                color: !selected ? "#94A3B8" : "#64748B",
                              }}
                            >
                              {!selected ? "Select Rollup" : selected}
                            </Typography>
                          )}
                          MenuProps={{
                            autoFocus: false,
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                borderRadius: "12px",
                              },
                            },
                          }}
                        >
                          {['1 WEEK', '4 WEEK', '13 WEEK', 'MTD', 'QTD', 'YTD','TTD'].map((name) => (
                            <MenuItem
                              key={name}
                              value={name}
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 500,
                              }}
                            >
                              <ListItemText
                                primary={name}
                                primaryTypographyProps={{
                                  fontSize: "0.85rem",
                                  fontWeight: 500,
                                }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  </Box>
                  )}

                  {/* Step 3: Geography Level */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: "16px",
                        border: "1px solid #EEF2F7",
                        bgcolor: "#FFFFFF",
                        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "#DBEAFE",
                          color: "#0576FC",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          flexShrink: 0,
                          fontSize: "0.8rem",
                          mt: 0.2,
                        }}
                      >
                        {showRollup ? 3 : 2}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "#1E293B",
                            lineHeight: 1.2,
                            mb: 0.5,
                            fontSize: "13px"
                          }}
                        >
                          Geography level
                        </Typography>
                        <Typography
                          sx={{
                            color: "#64748B",
                            fontWeight: 500,
                            display: "block",
                            mb: 2,
                            fontSize: "11px"
                          }}
                        >
                          {isApexOnly ? "Select at least one territory for Apex IC validation." : "Enable one or more levels. National is on by default."}
                        </Typography>
                        <Box
                          sx={{
                            // p: 2,
                            borderRadius: "16px",
                            // bgcolor: "#F8FAFC",
                          }}
                        >
                          {!isApexOnly && (
                          <Grid container spacing={1.5}>
                            {[
                              {
                                key: "National",
                                label: "Nation",
                                desc: "Overall validation",
                                icon: (
                                  <LanguageRoundedIcon sx={{ fontSize: 20 }} />
                                ),
                              },
                              {
                                key: "Region",
                                label: "Region",
                                desc: "Per-region rollups",
                                icon: <MapRoundedIcon sx={{ fontSize: 20 }} />,
                              },
                              {
                                key: "Territory",
                                label: "Territory",
                                desc: "Per-territory drill-down",
                                icon: (
                                  <PlaceRoundedIcon sx={{ fontSize: 20 }} />
                                ),
                              },
                            ].map((level) => {
                              const isActive = geoLevels[level.key];
                              const isOnlyHcpSummary = selectedReports.length === 1 && selectedReports.includes("HCP Summary");
                              const isDisabled = isOnlyHcpSummary && (level.key === "Region" || level.key === "Territory");
                              const isApexLocked = hasApexWithOtherReports && (level.key === "National" || level.key === "Territory");
                              return (
                                <Grid item xs={4} key={level.key}>
                                  <Box
                                    sx={{
                                      p: 1.6,
                                      borderRadius: "14px",
                                      bgcolor: isDisabled ? "#F8FAFC" : isActive
                                        ? "rgba(5,118,252,0.06)"
                                        : "#FFFFFF",
                                      border: "1px solid",
                                      borderColor: isActive
                                        ? "rgba(5,118,252,0.25)"
                                        : "#E2E8F0",
                                      boxShadow: isDisabled ? "none" : "0 1px 0 rgba(15, 23, 42, 0.04)",
                                      transition:
                                        "background-color 0.2s, border-color 0.2s, box-shadow 0.2s",
                                      cursor: isDisabled || isApexLocked ? "default" : "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      opacity: isDisabled ? 0.6 : 1,
                                      "&:hover": {
                                        borderColor: isDisabled ? "#E2E8F0" : isActive
                                          ? "rgba(5,118,252,0.3)"
                                          : "#CBD5E1",
                                        boxShadow: isDisabled ? "none" : "0 4px 14px rgba(15, 23, 42, 0.06)",
                                      },
                                    }}
                                    onClick={() => !isDisabled && !isApexLocked && toggleGeoLevel(level.key)}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          color: isActive
                                            ? "#1D4ED8"
                                            : "#94A3B8",
                                          display: "flex",
                                        }}
                                      >
                                        {level.icon}
                                      </Box>
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontWeight: 500,
                                            fontSize: "13px",
                                            color: "#1E293B",
                                          }}
                                        >
                                          {level.label}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "#94A3B8",
                                            fontSize: "0.6rem",
                                            display: "block",
                                            mt: -0.5,
                                          }}
                                        >
                                          {level.desc}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Switch
                                      size="small"
                                      disabled={isDisabled}
                                      checked={isActive}
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "#0576FC",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "rgba(5,118,252,0.35)",
                                        },
                                        "& .MuiSwitch-track": {
                                          backgroundColor: "#E2E8F0",
                                        },
                                      }}
                                    />
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                          )}

                          <Grid container spacing={2} sx={{ mt: isApexOnly ? 0 : 1 }}>
                            {geoLevels.Region && (
                              <Grid item xs={geoLevels.Territory ? 6 : 12}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 500,
                                    color: "#64748B",
                                    mb: 1,
                                    display: "block",
                                    ml: 0.5,
                                  }}
                                >
                                  Select regions
                                </Typography>
                                <Select
                                  multiple
                                  fullWidth
                                  size="small"
                                  value={selectedRegions}
                                  displayEmpty
                                  onChange={handleRegionChange}
                                  input={
                                    <OutlinedInput
                                      sx={{
                                        borderRadius: "10px",
                                        bgcolor: "#FFF",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "#E2E8F0",
                                        },
                                      }}
                                    />
                                  }
                                  renderValue={(selected) => (
                                    <Typography
                                      sx={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#64748B",
                                      }}
                                    >
                                      {selected.length} regions selected
                                    </Typography>
                                  )}
                                  MenuProps={{
                                    autoFocus: false,
                                    PaperProps: {
                                      sx: {
                                        maxHeight: 300,
                                        borderRadius: "12px",
                                      },
                                    },
                                  }}
                                >
                                  <ListSubheader
                                    sx={{
                                      p: 1,
                                      bgcolor: "#FFF",
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 1,
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Search regions..."
                                      value={regionSearch}
                                      onChange={(e) =>
                                        setRegionSearch(e.target.value)
                                      }
                                      onKeyDown={(e) => e.stopPropagation()}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <SearchIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "#94A3B8",
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                          bgcolor: "#F8FAFC",
                                        },
                                      }}
                                    />
                                  </ListSubheader>
                                  <MenuItem
                                    value="all"
                                    sx={{
                                      borderBottom: "1px solid #F1F5F9",
                                      py: 1,
                                    }}
                                  >
                                    <Checkbox
                                      checked={
                                        selectedRegions.length ===
                                        regionsList.length &&
                                        regionsList.length > 0
                                      }
                                      indeterminate={
                                        selectedRegions.length > 0 &&
                                        selectedRegions.length <
                                        regionsList.length
                                      }
                                      size="small"
                                    />
                                    <ListItemText
                                      primary="Select All"
                                      primaryTypographyProps={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#0576FC",
                                      }}
                                    />
                                  </MenuItem>
                                  {regionsList
                                    .filter((name) =>
                                      name
                                        .toLowerCase()
                                        .includes(regionSearch.toLowerCase()),
                                    )
                                    .map((name) => (
                                      <MenuItem
                                        key={name}
                                        value={name}
                                        sx={{
                                          fontSize: "0.85rem",
                                          fontWeight: 500,
                                        }}
                                      >
                                        <Checkbox
                                          checked={
                                            selectedRegions.indexOf(name) > -1
                                          }
                                          size="small"
                                        />
                                        <ListItemText
                                          primary={name}
                                          primaryTypographyProps={{
                                            fontSize: "0.85rem",
                                            fontWeight: 500,
                                          }}
                                        />
                                      </MenuItem>
                                    ))}
                                </Select>
                              </Grid>
                            )}

                            {geoLevels.Territory && (
                              <Grid item xs={geoLevels.Region ? 6 : 12}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 500,
                                    color: "#64748B",
                                    mb: 1,
                                    display: "block",
                                    ml: 0.5,
                                    fontSize: "12px"
                                  }}
                                >
                                  Select territories
                                </Typography>
                                <Select
                                  multiple
                                  fullWidth
                                  size="small"
                                  value={selectedTerritories}
                                  displayEmpty
                                  onChange={handleTerritoryChange}
                                  input={
                                    <OutlinedInput
                                      sx={{
                                        borderRadius: "10px",
                                        bgcolor: "#FFF",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "#E2E8F0",
                                        },
                                      }}
                                    />
                                  }
                                  renderValue={(selected) => (
                                    <Typography
                                      sx={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#64748B",
                                      }}
                                    >
                                      {selected.length} territories selected
                                    </Typography>
                                  )}
                                  MenuProps={{
                                    autoFocus: false,
                                    PaperProps: {
                                      sx: {
                                        maxHeight: 300,
                                        borderRadius: "12px",
                                      },
                                    },
                                  }}
                                >
                                  <ListSubheader
                                    sx={{
                                      p: 1,
                                      bgcolor: "#FFF",
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 1,
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Search territories..."
                                      value={territorySearch}
                                      onChange={(e) =>
                                        setTerritorySearch(e.target.value)
                                      }
                                      onKeyDown={(e) => e.stopPropagation()}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <SearchIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "#94A3B8",
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                          bgcolor: "#F8FAFC",
                                        },
                                      }}
                                    />
                                  </ListSubheader>
                                  <MenuItem
                                    value="all"
                                    sx={{
                                      borderBottom: "1px solid #F1F5F9",
                                      py: 1,
                                    }}
                                  >
                                    <Checkbox
                                      checked={
                                        selectedTerritories.length ===
                                        territoriesList.length &&
                                        territoriesList.length > 0
                                      }
                                      indeterminate={
                                        selectedTerritories.length > 0 &&
                                        selectedTerritories.length <
                                        territoriesList.length
                                      }
                                      size="small"
                                    />
                                    <ListItemText
                                      primary="Select All"
                                      primaryTypographyProps={{
                                        fontSize: "0.85rem",
                                        fontWeight: 500,
                                        color: "#0576FC",
                                      }}
                                    />
                                  </MenuItem>
                                  {territoriesList
                                    .filter((name) =>
                                      name
                                        .toLowerCase()
                                        .includes(
                                          territorySearch.toLowerCase(),
                                        ),
                                    )
                                    .map((name) => (
                                      <MenuItem
                                        key={name}
                                        value={name}
                                        sx={{
                                          fontSize: "0.85rem",
                                          fontWeight: 500,
                                        }}
                                      >
                                        <Checkbox
                                          checked={
                                            selectedTerritories.indexOf(name) >
                                            -1
                                          }
                                          size="small"
                                        />
                                        <ListItemText
                                          primary={name}
                                          primaryTypographyProps={{
                                            fontSize: "0.85rem",
                                            fontWeight: 500,
                                          }}
                                        />
                                      </MenuItem>
                                    ))}
                                </Select>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* HCP Summary Inputs Section */}
                  {selectedReports.includes("HCP Summary") && (
                    <Fade in={true}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #E2E8F0",
                          mb: 3,
                          boxShadow: "none",
                          bgcolor: "#FFF",
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2.5,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "#1E293B",
                                  fontSize: "14px",
                                }}
                              >
                                HCP Summary Inputs
                              </Typography>
                              <Typography
                                sx={{ color: "#64748B", fontWeight: 500, fontSize: "12px" }}
                              >
                                Provide all details (Region, Territory, NPI, Last Name) for targeted validation
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={addHcpSet}
                              startIcon={<AddRoundedIcon />}
                              sx={{
                                fontWeight: 500,
                                textTransform: "none",
                                borderRadius: "999px",
                                px: 1.6,
                                py: 0.8,
                                bgcolor: "#EFF6FF",
                                color: "#1D4ED8",
                                boxShadow: "none",
                                border: "1px solid #BFDBFE",
                                "&:hover": {
                                  bgcolor: "#DBEAFE",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              Add New Set
                            </Button>
                          </Box>
                          {/* Labels Header - shown once */}
                          <Grid container spacing={2} sx={{ mb: 1, px: 2 }}>
                            {["Region", "Territory", "NPI", "Last Name"].map(
                              (label) => (
                                <Grid item xs={2.8} key={label}>
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: "#64748B",
                                      ml: 0.5,
                                      fontSize: "12px"
                                    }}
                                  >
                                    {label.toUpperCase()}{" "}
                                    <span style={{ color: "#EF4444" }}>*</span>
                                  </Typography>
                                </Grid>
                              ),
                            )}
                            <Grid item xs={0.8} />
                          </Grid>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                              mb: 2,
                            }}
                          >
                            {hcpSets.map((set) => (
                              <Box
                                key={set.id}
                                sx={{
                                  p: 1.5,
                                  borderRadius: "14px",
                                  border: "1px solid #E2E8F0",
                                  bgcolor: "#F8FAFC",
                                  transition: "all 0.2s",

                                  "&:hover": {
                                    bgcolor: "#FFF",
                                    borderColor: "#0576FC",
                                    boxShadow:
                                      "0 4px 12px -4px rgba(37, 99, 235, 0.1)",
                                  },
                                }}
                              >
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={2.8}>
                                    <Select
                                      fullWidth
                                      size="small"
                                      value={set.region}
                                      onChange={(e) =>
                                        updateHcpSet(
                                          set.id,
                                          "region",
                                          e.target.value,
                                        )
                                      }
                                      displayEmpty
                                      MenuProps={{
                                        autoFocus: false,
                                        PaperProps: {
                                          sx: { maxHeight: 320, borderRadius: "12px" },
                                        },
                                      }}
                                      input={
                                        <OutlinedInput
                                          sx={{
                                            borderRadius: "8px",
                                            bgcolor: "#FFF",
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                          }}
                                        />
                                      }
                                      renderValue={(selected) => {
                                        if (!selected)
                                          return (
                                            <Typography
                                              sx={{
                                                color: "#94A3B8",
                                                fontSize: "0.75rem",
                                                fontWeight: 500,
                                              }}
                                            >
                                              Select Region
                                            </Typography>
                                          );
                                        return (
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {selected}
                                          </Typography>
                                        );
                                      }}
                                    >
                                      <ListSubheader
                                        sx={{
                                          p: 1,
                                          bgcolor: "#FFF",
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 1,
                                        }}
                                      >
                                        <TextField
                                          size="small"
                                          fullWidth
                                          placeholder="Search regions..."
                                          value={hcpRegionSearch}
                                          onChange={(e) =>
                                            setHcpRegionSearch(e.target.value)
                                          }
                                          onKeyDown={(e) => e.stopPropagation()}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <SearchIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "#94A3B8",
                                                  }}
                                                />
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "10px",
                                              bgcolor: "#F8FAFC",
                                              fontSize: "0.75rem",
                                              fontWeight: 500,
                                            },
                                          }}
                                        />
                                      </ListSubheader>
                            
                                      {regionsList
                                        .filter((name) =>
                                          name
                                            .toLowerCase()
                                            .includes(hcpRegionSearch.toLowerCase()),
                                        )
                                        .map((name) => (
                                          <MenuItem
                                            key={name}
                                            value={name}
                                            sx={{
                                              fontSize: "0.75rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </Grid>
                                  <Grid item xs={2.8}>
                                    <Select
                                      fullWidth
                                      size="small"
                                      value={set.territory}
                                      onChange={(e) =>
                                        updateHcpSet(
                                          set.id,
                                          "territory",
                                          e.target.value,
                                        )
                                      }
                                      displayEmpty
                                      MenuProps={{
                                        autoFocus: false,
                                        PaperProps: {
                                          sx: { maxHeight: 320, borderRadius: "12px" },
                                        },
                                      }}
                                      input={
                                        <OutlinedInput
                                          sx={{
                                            borderRadius: "8px",
                                            bgcolor: "#FFF",
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                          }}
                                        />
                                      }
                                      renderValue={(selected) => {
                                        if (!selected)
                                          return (
                                            <Typography
                                              sx={{
                                                color: "#94A3B8",
                                                fontSize: "0.75rem",
                                                fontWeight: 500,
                                              }}
                                            >
                                              Select Territory
                                            </Typography>
                                          );
                                        return (
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {selected}
                                          </Typography>
                                        );
                                      }}
                                    >
                                      <ListSubheader
                                        sx={{
                                          p: 1,
                                          bgcolor: "#FFF",
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 1,
                                        }}
                                      >
                                        <TextField
                                          size="small"
                                          fullWidth
                                          placeholder="Search territories..."
                                          value={hcpTerritorySearch}
                                          onChange={(e) =>
                                            setHcpTerritorySearch(e.target.value)
                                          }
                                          onKeyDown={(e) => e.stopPropagation()}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <SearchIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "#94A3B8",
                                                  }}
                                                />
                                              </InputAdornment>
                                            ),
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "10px",
                                              bgcolor: "#F8FAFC",
                                              fontSize: "0.75rem",
                                              fontWeight: 500,
                                            },
                                          }}
                                        />
                                      </ListSubheader>
                                     
                                      {territoriesList
                                        .filter((name) =>
                                          name
                                            .toLowerCase()
                                            .includes(hcpTerritorySearch.toLowerCase()),
                                        )
                                        .map((name) => (
                                          <MenuItem
                                            key={name}
                                            value={name}
                                            sx={{
                                              fontSize: "0.75rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </Grid>
                                  <Grid item xs={2.8}>
                                    <TextField
                                      placeholder="Enter NPI"
                                      size="small"
                                      fullWidth
                                      type="text"
                                      inputProps={{
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                      }}
                                      value={set.npi}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d*$/.test(val)) {
                                          updateHcpSet(
                                            set.id,
                                            "npi",
                                            val,
                                          );
                                        }
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                          bgcolor: "#FFF",
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={2.8}>
                                    <TextField
                                      placeholder="Enter Last Name"
                                      size="small"
                                      fullWidth
                                      value={set.lastName}
                                      onChange={(e) =>
                                        updateHcpSet(
                                          set.id,
                                          "lastName",
                                          e.target.value,
                                        )
                                      }
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "8px",
                                          bgcolor: "#FFF",
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    xs={0.8}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => removeHcpSet(set.id)}
                                      disabled={hcpSets.length === 1}
                                      sx={{
                                        color:
                                          hcpSets.length === 1
                                            ? "#CBD5E1"
                                            : "#EF4444",
                                        bgcolor: "transparent",
                                        "&:hover": {
                                          color: "#EF4444",
                                          bgcolor: "#FEF2F2",
                                        },

                                      }}
                                    >
                                      <DeleteRoundedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  )}

                </DialogContent>
                <DialogActions
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: "1px solid #F1F5F9",
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 -10px 24px rgba(15, 23, 42, 0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 22,
                      opacity: validationError ? 1 : 0,
                      transition: "opacity 0.2s",
                      flex: 1,
                    }}
                  >
                    <WarningRoundedIcon sx={{ color: "#EF4444", fontSize: 18 }} />
                    <Typography
                      sx={{
                        color: "#EF4444",
                        fontWeight: 500,
                        fontSize: "0.8rem",
                      }}
                    >
                      {validationError}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    disabled={isRunning}
                    onClick={handleRun}
                    sx={{
                      borderRadius: "12px",
                      py: 1.1,
                      px: 3.2,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      bgcolor: "#0576FC",
                      textTransform: "none",
                      minWidth: "200px",
                      boxShadow: "0 10px 26px rgba(5, 118, 252, 0.22)",
                      "&:hover": {
                        bgcolor: "#1D4ED8",
                        boxShadow: "0 14px 30px rgba(5, 118, 252, 0.26)",
                      },
                    }}
                  >
                    {isRunning ? "Triggering..." : "Confirm & Run Validation"}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Fade>
        ) : (
          <Fade in={activeTab === 1}>
            <Box>
              <ValidationHistory
                reports={reports}
                initialRunId={selectedRunId}
                onClearRunId={() => setSelectedRunId(null)}
                data={viewResultForRun}
                onOpenHcpModal={handleOpenHcpModal}
                onOpenGeoModal={handleOpenGeoSummaryModal}
                
                


              />
            </Box>
          </Fade>
        )}
      </Box>
      <Dialog
        open={geoSummaryModalOpen}
        onClose={handleCloseGeoSummaryModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 28px 60px -18px rgb(15 23 42 / 0.32)",
            bgcolor: "#FFF",
            m: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.6,
            py: 2.1,
            borderBottom: "1px solid #EEF2F7",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* <Box
               sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: "10px",
 bgcolor: "#ECFDF5",
color: "#059669",
border: "1px solid #A7F3D0"
  }}
 
            >
              <PublicIcon sx={{ fontSize: 21 }} />
            </Box> */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: "#1E293B", fontSize: "15px" }}
              >
                Selected Regions & Territories
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748B",
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 700,
                  mt: 0.35,
                }}
              >
                Validation parameters
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseGeoSummaryModal}
            size="small"
            sx={{
              color: "#64748B",
              bgcolor: "#F8FAFC",
              height: 38,
              width: 38,
              "&:hover": { bgcolor: "#2563EB", color: "#FFF" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#FBFCFE", p: 2.6, pb: 2.8 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: "14px",
                  border: "1px solid #DDE7F3",
                  boxShadow: "none",
                  height: "100%",
                  bgcolor: "#FFFFFF",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 1.8, "&:last-child": { pb: 1.8 } }}>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      gap: 1,
                      mb: 1.4,
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: "center",
                        bgcolor: "#EFF6FF",
                        borderRadius: "10px",
                        color: "#2563EB",
                        display: "flex",
                        height: 38,
                        justifyContent: "center",
                        width: 38,
                      }}
                    >
                      <LanguageRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "13px",
                        color: "#1E293B",
                        lineHeight: 1.2,
                      }}
                    >
                      {selectedGeoSummary.regions.length > 1
                        ? "Regions"
                        : "Region"}{" "}
                      ({selectedGeoSummary.regions.length})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: "1px solid #E6EDF5",
                      borderRadius: "12px",
                      p: 1,
                      bgcolor: "#F8FAFC",
                    }}
                  >
                    {selectedGeoSummary.regions.length === 0 ? (
                      <Typography
                        sx={{
                          color: "#94A3B8",
                          fontSize: "12px",
                          fontWeight: 600,
                          py: 1,
                          textAlign: "center",
                        }}
                      >
                        No region selected
                      </Typography>
                    ) : (
                      selectedGeoSummary.regions
                        .filter((region) =>
                          region
                            ?.toLowerCase()
                            .includes(geoRegionSearch.toLowerCase()),
                        )
                        .map((region) => (
                          <Typography
                            key={region}
                            sx={{
                              bgcolor: "#FFFFFF",
                              border: "1px solid #E1E9F2",
                              borderRadius: "10px",
                              color: "#475569",
                              fontSize: "12px",
                              fontWeight: 700,
                              mb: 0.7,
                              px: 1.2,
                              py: 0.75,
                            }}
                          >
                            {region}
                          </Typography>
                        ))
                    )}
                    {selectedGeoSummary.regions.length > 0 &&
                      selectedGeoSummary.regions.filter((region) =>
                        region
                          ?.toLowerCase()
                          .includes(geoRegionSearch.toLowerCase()),
                      ).length === 0 && (
                        <Typography
                          sx={{
                            color: "#94A3B8",
                            fontSize: "12px",
                            fontWeight: 600,
                            py: 1,
                            textAlign: "center",
                          }}
                        >
                          No region selected
                        </Typography>
                      )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: "14px",
                  border: "1px solid #DDE7F3",
                  boxShadow: "none",
                  height: "100%",
                  bgcolor: "#FFFFFF",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 1.8, "&:last-child": { pb: 1.8 } }}>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      gap: 1,
                      mb: 1.4,
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: "center",
                        bgcolor: "#F0FDF4",
                        borderRadius: "10px",
                        color: "#16A34A",
                        display: "flex",
                        height: 38,
                        justifyContent: "center",
                        width: 38,
                      }}
                    >
                      <PlaceRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "13px",
                        color: "#1E293B",
                        lineHeight: 1.2,
                      }}
                    >
                      {selectedGeoSummary.territories.length > 1
                        ? "Territories"
                        : "Territory"}{" "}
                      ({selectedGeoSummary.territories.length})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: "1px solid #E6EDF5",
                      borderRadius: "12px",
                      p: 1,
                      bgcolor: "#F8FAFC",
                    }}
                  >
                    {selectedGeoSummary.territories.length === 0 ? (
                      <Typography
                        sx={{
                          color: "#94A3B8",
                          fontSize: "12px",
                          fontWeight: 600,
                          py: 1,
                          textAlign: "center",
                        }}
                      >
                        No territory selected
                      </Typography>
                    ) : (
                      selectedGeoSummary.territories
                        .filter((territory) =>
                          territory
                            ?.toLowerCase()
                            .includes(geoTerritorySearch.toLowerCase()),
                        )
                        .map((territory) => (
                          <Typography
                            key={territory}
                            sx={{
                              bgcolor: "#FFFFFF",
                              border: "1px solid #E1E9F2",
                              borderRadius: "10px",
                              color: "#475569",
                              fontSize: "12px",
                              fontWeight: 700,
                              mb: 0.7,
                              px: 1.2,
                              py: 0.75,
                            }}
                          >
                            {territory}
                          </Typography>
                        ))
                    )}
                    {selectedGeoSummary.territories.length > 0 &&
                      selectedGeoSummary.territories.filter((territory) =>
                        territory
                          ?.toLowerCase()
                          .includes(geoTerritorySearch.toLowerCase()),
                      ).length === 0 && (
                        <Typography
                          sx={{
                            color: "#94A3B8",
                            fontSize: "12px",
                            fontWeight: 600,
                            py: 1,
                            textAlign: "center",
                          }}
                        >
                          No territory selected
                        </Typography>
                      )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {/* HCP Inputs Modal (Result View) */}
      <Dialog
        open={hcpModalOpen}
        onClose={handleCloseHcpModal}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            bgcolor: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(6px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.16)",
            bgcolor: "#FFF",
            m: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid #F1F5F9",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* <Box
              sx={{
                p: 0.8,
                borderRadius: "8px",
                bgcolor: "#0576FC",
                color: "#FFF",
                display: "flex",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
              }}
            >
              <TuneRoundedIcon sx={{ fontSize: 18 }} />
            </Box> */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#1E293B", lineHeight: 1.2 }}
              >
                HCP Summary inputs
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#64748B", fontWeight: 500 }}
              >
                Validation parameters
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseHcpModal}
            size="small"
            sx={{
              color: "#64748B",
              bgcolor: "#F8FAFC",
              "&:hover": { bgcolor: "#0576FC", color: "#FFF" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pb: 3 }}>
          <Card
            sx={{
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              boxShadow: "none",
              bgcolor: "#FFF",
              overflow: "hidden",
            }}
          >
            <TableContainer sx={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["REGION", "TERRITORY", "NPI", "LAST NAME"].map((head) => (
                      <TableCell
                        key={head}
                        sx={{
                          fontWeight: 600,
                          color: "#64748B",
                          fontSize: "0.65rem",
                          py: 1.5,
                          px: 2,
                          bgcolor: "#F8FAFC",
                          borderBottom: "2px solid #E2E8F0",
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedHcpInputs.map((input, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ "&:hover": { bgcolor: "#FBFBFE" } }}
                    >
                      <TableCell
                        sx={{
                          color: "#1E293B",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          py: 1.2,
                          px: 2,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        {input.region || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#475569",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          px: 2,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        {input.territory || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#0576FC",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          px: 2,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        {input.npi || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#475569",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          px: 2,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        {input.lastname || input.lastName || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RunValidation;

